import { Action } from 'redux';
import { cancel, put, take, select, call, fork, takeEvery } from 'redux-saga/effects';
import { List, Map, Set } from 'immutable';
import log from 'loglevel';
import uuid from 'uuid';

import { request, notify } from '../remote/sagas';
import { wCall } from '../wait/sagas';
import { showAlert } from '../alert/sagas';

import { syncResource } from '../sync/actions';
import {
    PUSH_VIEW,
    pushView,
    REPLACE_VIEW,
    replaceView,
    SET_ROOT_VIEW,
    setRootView,
    replaceRootAndCurrentView,
    POP_VIEW,
    popView,
    REQUEST_POP_VIEW,
    requestPopView,
    OPEN_VIEW,
    openView as openViewAction,
    REPLACE_ROOT_AND_CURRENT_VIEW,
} from './actions';
import { setQuestionActivity, removeQuestionActivity } from '../question-activity/actions';
import { setViewState, taskViewOpened, updateViewState } from '../viewstates/actions';
import { analyticsHit } from '../analytics/actions';
import { updateAppState, toggleHandsUp } from '../appstate/actions';

import { getAppState, isTaskLocked, getPopLastViewURL } from '../appstate/selectors';
import { getTaskState, getTaskItemStates } from '../progress/selectors';
import { ABORT_CURRENT_TASKS_EXCEPTION } from 'phoenix2/exceptions';
import { isImmutableException } from 'phoenix2/utils/exceptions';
import {
    getQuestionActivities,
    getQuestionActivityForTaskItem,
    getQuestionActivityId,
    getQuestionActivityStage,
    getShowingQuestionAttemptId,
    isActiveQuestionSubmitted,
} from '../question-activity/selectors';
import { selectPreviousView, selectCurrentView } from './selectors';
import { QUESTION_STAGES } from '../question-activity/reducers';
import { onRequestSkillDeliveryViewPop } from '../hands-up/sagas';
import { openGameTaskView } from 'phoenix2/state/games/actions';
import { selectLQDTaskItem } from 'phoenix2/state/lqd/actions';
import { openQuizTaskView } from 'phoenix2/state/quiz/sagas';
import { ensureSynced } from 'phoenix2/state/sync/sagas';
import { abortExistingAttempt } from '../../discovery/state/practice/sagas';
import { openSurveyAnswerView } from '../../discovery/state/lsi/sagas';
import { PACKAGE_LIST_VIEW_ID, TASK_VIEW_ID, SKILL_DELIVERY_VIEW_ID } from '../viewstates/view-ids';
import { openQuickFireTaskView } from '../../discovery/state/quickfire/sagas';
import { getTask } from 'phoenix2/state/content/selectors_ts';
import { cancelWAC, closeWACOverlay } from 'phoenix2/state/wac/actions';
import { Action as ViewAction, View, ViewsState } from './reducers';
import {
    IImmutableTask,
    IImmutableTaskItemState,
    IImmutableTaskState,
} from 'phoenix2/state/sparxweb/apis/content.types';
import { Task } from 'redux-saga';

function taskCanBeUsed(task: IImmutableTask | undefined): task is IImmutableTask {
    return task
        ? task.get('items').some(item => item?.get('activity_id') !== 'NONE') ||
              task.get('task_type') === 'GAME'
        : false;
}

/**
 * Saga to detect when the user changes page within the app, and then
 * send this info to analytics. In Sparx Web, a "page" is considered to be
 * a view, except for the skill delivery view where each page is a page in
 * its own right.
 */
export function* sendScreenViews() {
    while (true) {
        const action: ViewAction = yield take([
            PUSH_VIEW,
            REPLACE_VIEW,
            SET_ROOT_VIEW,
            REPLACE_ROOT_AND_CURRENT_VIEW,
            POP_VIEW,
        ]);
        let page: string | null = null;
        if (action.type === POP_VIEW) {
            // Returning to a previous view:
            const currentView: View | undefined = yield select(selectCurrentView);

            if (currentView) {
                page = currentView.type;
            }
        } else {
            // Going to a new page:
            page = action.view.type;
        }

        // Send an analytics event that this page has been viewed (unless on
        // skill delivery view which sends its own events, due to having
        // multiple pages)
        if (page && page !== SKILL_DELIVERY_VIEW_ID) {
            yield put(analyticsHit('pageview', { page }));
        }
    }
}

/**
 * Waits for the current view to pop. Takes into account views being pushed
 * on top of it/it being transient.
 * Returns the action that caused the view to pop.
 */
export function* waitForPopView(
    isTransient = false,
    onRequestPop: any = null,
    viewId: string | null = null,
) {
    // The number of views pushed on top of this one.
    const stack = [viewId];
    let requestPopTask: Task | null = null;
    let lastPushedViewWasTransient = false;
    try {
        // Listen for view actions. If the action means the current view would
        // be removed, return with that action. If it doesn't then continue.
        while (true) {
            const closeAction: ViewAction | ReturnType<typeof requestPopView> = yield take([
                PUSH_VIEW,
                POP_VIEW,
                REPLACE_VIEW,
                SET_ROOT_VIEW,
                REPLACE_ROOT_AND_CURRENT_VIEW,
                REQUEST_POP_VIEW,
            ]);

            switch (closeAction.type) {
                case PUSH_VIEW:
                    // If this view is transient, pushing any view removes it.
                    if (isTransient) {
                        return closeAction;
                    }

                    if (lastPushedViewWasTransient) {
                        stack.pop();
                    }
                    stack.push(closeAction.view.viewId);
                    lastPushedViewWasTransient = !!closeAction.transient;
                    break;
                case POP_VIEW:
                    stack.pop();
                    if (stack.length === 0) {
                        return closeAction;
                    }
                    if (closeAction.backToViewId && stack.length > 0) {
                        while (stack[stack.length - 1] !== closeAction.backToViewId) {
                            stack.pop();
                            if (stack.length === 0) {
                                return closeAction;
                            }
                        }
                    }
                    lastPushedViewWasTransient = false;
                    break;
                case REPLACE_VIEW:
                    if (stack.length === 1) {
                        return closeAction;
                    }
                    lastPushedViewWasTransient = !!closeAction.transient;
                    break;
                case SET_ROOT_VIEW:
                case REPLACE_ROOT_AND_CURRENT_VIEW:
                    return closeAction;
                case REQUEST_POP_VIEW:
                    if (stack.length === 1 && onRequestPop) {
                        if (requestPopTask) {
                            cancel(requestPopTask);
                        }
                        requestPopTask = yield fork(onRequestPop);
                    }
                    break;
            }
        }
    } finally {
        if (requestPopTask) {
            cancel(requestPopTask);
        }
    }
}

const OPEN_VIEW_ACTION_CREATORS = {
    push: pushView,
    replace: replaceView,
    setRoot: setRootView,
    replaceRootAndCurrent: replaceRootAndCurrentView,
};

export type OpenViewMethod = keyof typeof OPEN_VIEW_ACTION_CREATORS;

type OpenViewOptions = {
    transient?: boolean;
    previous?: View;
    onViewOpenedAction?: Action;
    onRequestPop?: any;
};

/**
 * Opens a view with the method provided.
 * method  - one of OPEN_VIEW_ACTION_CREATORS above
 * view    - Immutable map containing viewId and type
 * options - object optionally containing:
 *      transient: if true view disappears whenever another view is pushed
 *      onViewOpenedAction: an action to fire when the view is opened
 *      onRequestPop: a function to delegate to when the view is popped
 *          (this function should put a popView in some scenario)
 *
 * TODO: pop/replace view should provide the viewId of the view to pop/replace
 */
export function* openView(
    method: OpenViewMethod,
    view: View,
    options: OpenViewOptions = {},
): Generator<any, any, any> {
    // Open the view.
    const actionCreator = OPEN_VIEW_ACTION_CREATORS[method];
    if (!actionCreator) {
        throw `Unknown method to open view: ${method}`;
    }
    yield put(actionCreator(view, options));

    // Fire the view opened action.
    const onViewOpenedAction = options.onViewOpenedAction;
    if (onViewOpenedAction) {
        yield put(onViewOpenedAction);
    }

    // Wait for the view to close.
    const isTransient = options.transient;
    let onRequestPop: () => void = options.onRequestPop;

    // If there is no custom noRequestPop then the default action should be to pop the view
    if (!onRequestPop) {
        onRequestPop = function*() {
            const closeAction = popView();
            yield put(closeAction);
        };
    }

    return yield call(waitForPopView, isTransient, onRequestPop, view.viewId);
}

function* openLQDTask(taskID: string) {
    const task: IImmutableTask | undefined = yield select(getTask, taskID);
    if (task && task.get('items') && task.get('items').size > 0) {
        const taskItemStates: List<{ state: IImmutableTaskItemState }> = yield select(state =>
            getTaskItemStates(state, task.get('items')),
        );
        const firstNonCorrectTaskItemIx = taskItemStates.findIndex(
            taskItemState => !taskItemState?.state.get('done'),
        );
        const taskItemIndex = firstNonCorrectTaskItemIx === -1 ? 0 : firstNonCorrectTaskItemIx;
        yield put(selectLQDTaskItem(task.get('items').get(taskItemIndex)));
    } else {
        throw new Error(`LQD task with ID ${taskID} could not be started: $task`);
    }
}

function makeTaskErrorMessage(task: IImmutableTask | undefined, taskId: string) {
    return `Task ${taskId} cannot be used ${!task ? ' (not found)' : ''}`;
}

function* cleanupLQD() {
    // If the user was part way through a question when they left LQD then we exit that attempt.
    const currentAttemptId: string | undefined = yield select(getShowingQuestionAttemptId);
    if (currentAttemptId) {
        const stage: string = yield select(getQuestionActivityStage, currentAttemptId);
        const activityID: string | undefined = yield select(
            getQuestionActivityId,
            currentAttemptId,
        );
        if (stage === QUESTION_STAGES.question) {
            yield call(notify, 'exitTaskItem', activityID);
        }
    }

    // Clear up all of the question activities.
    const activities: any[] = yield select(getQuestionActivities);
    for (const act of activities.entries()) {
        yield put(removeQuestionActivity(act[0]));
    }
}

/**
 * When a student is locked into a task during a lesson and they try to do
 * something that is not allowed, shows alerts that will inform them of
 * this, and allow the teacher to type in a 4-digit pin to override the lock
 * @param title Title for the alert
 * @param message Message for the alert
 * @returns {boolean} Returns true if the lock is overridden, false if not
 */
export function* doCheckTeacherOverride(title: string, message: string) {
    // Firstly, show an alert informing the student they are locked in, giving
    // them the choice of going back, or proceeding to the override alert
    const choice: string = yield call(showAlert, {
        title,
        message,
        buttons: [
            { key: 'back', text: 'OK', secondary: true },
            { key: 'override', text: 'Teacher Override', primary: true },
        ],
    });
    // NOTE: AbortCurrentTasks exception that can be thrown from this alert
    // is handled higher up

    if (choice !== 'override') {
        return false;
    }

    // Secondly, show the alert the allows the teacher to enter a 4-digit
    // code to unlock the alert. Note, the correct code is generated by a
    // heuristic (it is the DDMM of the Monday of the current week), and
    // checking the teacher has entered the correct code is handled
    // internally within the alert
    const overriddenChoice: string = yield call(showAlert, {
        type: 'teacherOverride',
        title: 'Teacher Override',
        noDefaultButton: true,
    });
    // NOTE: AbortCurrentTasks exception that can be thrown from this alert
    // is handled higher up

    return overriddenChoice === 'overridden';
}

export function* onRequestPopTaskView(taskId: string) {
    try {
        const appState: Map<string, any> = yield select(getAppState);
        const lockTasks = appState.get('lockTasks');
        const taskState: IImmutableTaskState = yield select(getTaskState, taskId);
        if (!lockTasks) {
            yield put(popView());
            return;
        }
        if (taskState.get('complete')) {
            yield put(popView());
        }

        const overridden: boolean = yield call(
            doCheckTeacherOverride,
            'Locked in task',
            'Complete this task before starting the next one',
        );

        if (overridden) {
            yield put(updateAppState('lockTasks', false));
            yield put(updateAppState('activeTasks', null));
            yield put(popView());
            return;
        }
    } catch (exc) {
        if (!isImmutableException(exc)) {
            throw exc;
        }

        if (exc.get('type') === ABORT_CURRENT_TASKS_EXCEPTION) {
            // If an abort has been received during this process (e.g.
            // lesson switched to new task), just carry on
            log.debug('onRequestPopTaskView cancelled due to an abort current tasks request');
        } else {
            log.warn('onRequestPopTaskView cancelled due to unexpected exception', exc);
            throw exc;
        }
    }
}

export function* openIndyLQDTaskView({ taskId }: { taskId: string }) {
    const task: IImmutableTask | undefined = yield select(getTask, taskId);
    if (!taskCanBeUsed(task)) {
        const errorMessage = makeTaskErrorMessage(task, taskId);
        log.error(errorMessage);
        return;
    }
    yield put(syncResource('taskItemStates', taskId));
    yield put(syncResource('taskAssets', taskId));
    yield put(setViewState('task', Map({ taskId })));

    // Wait for the state for this task to be loaded.
    yield call(ensureSynced, 'taskItemStates', taskId);
    yield call(openLQDTask, taskId);

    try {
        yield call(
            openView,
            'push',
            {
                type: 'lqd',
                viewId: TASK_VIEW_ID,
            },
            {
                onViewOpenedAction: taskViewOpened(),
                onRequestPop: () => onRequestPopTaskView(taskId),
            },
        );
        yield put(updateViewState('task', { closed: true }));
    } finally {
        yield call(cleanupLQD);
    }
}

export function* updateCurrentTask(taskId: string) {
    // If the task is complete, update the current task to the next task
    const taskState: IImmutableTaskState = yield select(getTaskState, taskId);

    if (taskState.get('complete')) {
        const task: IImmutableTask = yield select(getTask, taskId);
        const next = task.getIn(['delivery_parameters', 'nextTaskId'], null);

        // Unlock all tasks if there is no next task - in a lesson this
        // means the student has finished the current phase of the
        // lesson and can do whatever they like now
        if (!next) {
            log.debug('Unlocking tasks as there is no next task');
            yield put(updateAppState('lockTasks', false));
        }

        yield put(updateAppState('activeTasks', next && Set([next])));
    }
}

/**
 * Opens the standard task view.
 */
export function* openStandardTaskView(taskId: string, method: OpenViewMethod = 'push') {
    const viewType = 'task';

    yield call(
        openView,
        method,
        {
            type: viewType,
            viewId: TASK_VIEW_ID,
        },
        {
            onViewOpenedAction: taskViewOpened(),
            onRequestPop: () => onRequestPopTaskView(taskId),
            previous: { type: 'packagelist', viewId: PACKAGE_LIST_VIEW_ID },
        },
    );
    yield put(cancelWAC());
    yield put(closeWACOverlay());
}

/**
 * Opens the confidence task view.
 */
export function* openConfidenceTaskView(taskId: string) {
    const taskState: IImmutableTaskState = yield select(getTaskState, taskId);
    const complete = taskState.get('complete', false);

    yield put(setViewState('task-' + taskId, Map({ taskId })));
    try {
        yield call(
            openView,
            'push',
            {
                type: complete ? 'confidence-overview' : 'confidence',
                viewId: `${TASK_VIEW_ID}-${taskId}`,
            },
            {
                onViewOpenedAction: taskViewOpened(),
                onRequestPop: () => onRequestPopTaskView(taskId),
            },
        );
    } finally {
        // If we're in a task item, dismiss it
        yield abortExistingAttempt();
        yield put(cancelWAC());
        yield put(closeWACOverlay());
    }
}

export function* openTaskView({
    taskId,
    options = {},
}: {
    taskId: string;
    options: { method?: OpenViewMethod; state?: any };
}) {
    try {
        const locked: boolean = yield select(isTaskLocked, taskId);
        if (locked) {
            const overridden: boolean = yield call(
                doCheckTeacherOverride,
                'Task is locked',
                'Please choose an available task',
            );
            if (!overridden) {
                return;
            }
            yield put(updateAppState('lockTasks', false));
            yield put(updateAppState('activeTasks', null));
        }
        const task: IImmutableTask | undefined = yield select(getTask, taskId);
        if (!taskCanBeUsed(task)) {
            const errorMessage = makeTaskErrorMessage(task, taskId);
            log.error(errorMessage);
            return;
        }
        if (task === undefined) {
            throw 'taskCanBeUsed guarantees the task is defined';
        }
        const splashScreen = task && task.get('splash_screen');
        if (splashScreen) {
            yield call(showAlert, {
                message: splashScreen.get('instructions'),
                image: splashScreen.get('image'),
            });
        }
        yield put(syncResource('taskItemStates', taskId));
        yield put(syncResource('taskAssets', taskId));
        yield put(setViewState('task', Map({ taskId })));
        // Check the task type to determine which task view to open
        switch (task.get('task_type')) {
            // The 'GAME' case dispatches an action to improve modularity
            case 'GAME':
                yield put(openGameTaskView(taskId, options));
                return;

            case 'INDY_QUIZ':
            case 'PROTOTYPE_ASSESSMENTS':
                yield call(openQuizTaskView, task, options.method, options.state);
                break;

            // TODO: feature_flag: lsi
            case 'LSI_SURVEY':
                yield call(openSurveyAnswerView, taskId, task, options.state);
                break;

            // TODO: feature_flag: quickfire
            case 'QUICK_FIRE':
                yield call(openQuickFireTaskView, taskId);
                break;

            // TODO: feature_flag: mpa
            case 'CONFIDENCE':
                yield call(openConfidenceTaskView, taskId);
                break;

            default:
                yield call(openStandardTaskView, taskId, options.method);
        }

        yield call(updateCurrentTask, taskId);
        yield put(updateViewState('task', { closed: true }));
    } catch (exc) {
        if (!isImmutableException(exc)) {
            throw exc;
        }

        if (exc.get('type') === ABORT_CURRENT_TASKS_EXCEPTION) {
            // If an abort has been received during this process (e.g.
            // lesson switched to new task, whilst showing the teacher
            // override or activity splash screen), just return from the
            // saga
            log.debug('onOpenTaskView cancelled due to an abort current tasks request');
        } else {
            log.warn('onOpenTaskView cancelled due to unexpected exception', exc);
            throw exc;
        }
    }
}

/**
 * Creates a new attempt of a question (generating an id for the attempt,
 * and putting it in the question-activity state).
 * @param info The question data
 * @param taskItemId the id of the task item (might be null, in tethering mode)
 * @param jumpToStage question stage to start at, by default start at question
 * @param reuseAttempt if to reuse any existing attempt for this taskItemId
 * @returns {*}
 */
export function* createAttempt(
    info: any,
    taskItemId: string | null,
    jumpToStage = QUESTION_STAGES.question,
    reuseAttempt = false,
) {
    if (reuseAttempt) {
        // Find existing question activities and use that
        const existing: string | undefined = yield select(
            getQuestionActivityForTaskItem,
            taskItemId,
        );
        if (existing) {
            return existing;
        }
    }

    const attemptId = uuid.v4();
    yield put(setQuestionActivity(attemptId, info, taskItemId, jumpToStage));
    return attemptId;
}

export function* setupSkillDeliveryView(
    info: any,
    taskItemId: string | null = null,
    checkingMode = false,
    jumpToStage = undefined,
    reuseAttempt = false,
    activityActionType = 'QUESTION',
) {
    // Cancel any hands up that is currently active. This is needed because
    // hands up can get triggered by the server due to a WAC, but we don't
    // want to show the hands up screen in this case
    yield put(toggleHandsUp(false));

    // Create a new attempt of the question:
    const attemptId: string = yield call(
        createAttempt,
        info,
        taskItemId,
        jumpToStage,
        reuseAttempt,
    );

    // Notify the server that the task item is shown to the user
    switch (activityActionType) {
        case 'QUIZ':
            yield wCall(request, 'showQuizTaskItem', info.get('question_activity_id'));
            break;
        default:
            yield call(notify, 'showQuestionTaskItem', info.get('question_activity_id'));
    }

    yield put(
        setViewState(
            'skilldelivery',
            Map({
                attemptId: attemptId,
                style: Map(),
                checkingMode,
            }),
        ),
    );

    return attemptId;
}

export function* openSkillDeliveryView(
    info: any,
    taskItemId: string | null = null,
    viewAction: OpenViewMethod = 'push',
    notifyServerOnExit = true,
    checkingMode = false,
    jumpToStage = undefined,
) {
    // Setup the view state and notify the server that we are making an attempt
    const attemptId: string = yield call(
        setupSkillDeliveryView,
        info,
        taskItemId,
        checkingMode,
        jumpToStage,
    );

    // Push the view.
    yield call(
        openView,
        viewAction,
        {
            type: 'skilldelivery',
            viewId: SKILL_DELIVERY_VIEW_ID,
        },
        {
            onRequestPop: onRequestSkillDeliveryViewPop,
        },
    );

    const wasAnswerSubmitted: boolean = yield select(isActiveQuestionSubmitted(attemptId));

    if (notifyServerOnExit && !wasAnswerSubmitted) {
        // If required, When the view closes, inform the server. We only do this
        // if an answer wasn't submitted - if it was, the server already knows:
        yield call(notify, 'exitTaskItem', info.get('question_activity_id'));
    }

    // Remove the question activity from the state:
    yield put(removeQuestionActivity(attemptId));
}

export function* watchOpenView() {
    yield takeEvery(OPEN_VIEW, ({ method, view, transient }: ReturnType<typeof openViewAction>) =>
        openView(method, view, { transient }),
    );
}

/**
 * Watches for REQUEST_POP_VIEW events, and if this is the last view in the
 * view stack, it will redirect to the popLastViewURL if there is one.
 * This is used in the New User app to take the user back to Sparx Web when they
 * press the browser back button or status bar back button
 * */
export function* watchRequestPopLastView() {
    yield takeEvery(REQUEST_POP_VIEW, function*() {
        const previousView: ViewsState | undefined = yield select(selectPreviousView);
        const url: string | undefined = yield select(getPopLastViewURL);
        if (!previousView && url) {
            window.location.replace(url);
        }
    });
}
